import { useState } from 'react';

import { Box, Button, Col, Dropdown, Link, Row, Text } from '@opendoor/bricks-next';
import type { ILpComponentOpenPositions } from 'declarations/contentful';

import { LandingPageSectionContainer } from './shared/Container';

// GROWTH-1355 POC (Path B — full API integration). Throwaway branch; do not ship.
// v1 endpoint returns all jobs in a single response with clean department labels;
// v2 carries the full HTML description + employmentType + companyName for JD pages.
const RIPPLING_BOARD_LIST_URL = 'https://api.rippling.com/platform/api/ats/v1/board/opendoor/jobs';
const ripplingDetailUrl = (uuid: string) =>
  `https://ats.rippling.com/api/v2/board/opendoor/jobs/${uuid}`;
const ripplingApplyUrl = (uuid: string) => `https://ats.rippling.com/opendoor/jobs/${uuid}/apply`;

interface RipplingV1Job {
  uuid: string;
  name: string;
  url: string;
  department: { id: string; label: string };
  workLocation: { id: string; label: string };
}

// One row per (uuid, name, dept) collapsed across the multiple location rows
// Rippling emits for multi-location reqs.
interface ListingJob {
  uuid: string;
  name: string;
  department: { id: string; label: string };
  locationLabels: string[];
}

interface RipplingV2Workplace {
  name?: string;
  country?: string;
  city?: string;
  state?: string;
  workplaceType?: string;
}

// v2 is inconsistent: some jobs return workLocations as objects, others as plain strings.
type RipplingV2WorkLocation = string | RipplingV2Workplace;

interface RipplingV2Job {
  uuid: string;
  name: string;
  description: { company: string; role: string };
  department: { name: string; base_department: string; department_tree: string[] };
  employmentType?: { id: string; label: string };
  createdOn: string;
  companyName: string;
  workLocations?: RipplingV2WorkLocation[];
  url: string;
}

const locName = (loc: RipplingV2WorkLocation): string =>
  typeof loc === 'string' ? loc : (loc.name ?? '');

const locWorkplace = (loc: RipplingV2WorkLocation): RipplingV2Workplace =>
  typeof loc === 'string' ? { name: loc } : loc;

const PROD_HOST = 'https://www.opendoor.com';

const slugify = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)
    .replace(/-+$/g, '');

const jobPath = (job: { uuid: string; name: string }): string =>
  `/careers/open-positions/jobs/${slugify(job.name)}-${job.uuid}`;

const jobAbsoluteUrl = (job: { uuid: string; name: string }): string =>
  `${PROD_HOST}${jobPath(job)}`;

const UUID_AT_END_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const extractJobUuid = (url: string): string | null => {
  try {
    const u = new URL(url, PROD_HOST);
    // Path-based: /careers/open-positions/jobs/<slug>-<uuid>
    const tailMatch = u.pathname.match(UUID_AT_END_RE);
    if (tailMatch) return tailMatch[0].toLowerCase();
    // Backwards compat: ?req_id=<uuid>
    const reqId = u.searchParams.get('req_id');
    if (reqId && UUID_AT_END_RE.test(reqId)) return reqId.toLowerCase();
  } catch {
    return null;
  }
  return null;
};

interface ListingResolved {
  departmentFilters: Array<{ label: string; value: string }>;
  officeFilters: Array<{ label: string; value: string }>;
  jobsByDepartment: Array<{ label: string; jobs: ListingJob[] }>;
}

interface DetailResolved {
  job: RipplingV2Job | null;
  jobPostingSchema: object | null;
  seoOverride: SeoOverride | null;
}

interface SeoOverride {
  title: string;
  canonicalUrl: string;
  metaDescription: string;
  ogTitle: string;
  ogUrl: string;
  ogDescription: string;
}

type Resolved = Partial<ListingResolved & DetailResolved>;

type DescriptionBlock =
  | { kind: 'heading'; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'list'; items: string[] };

const HEADING_RE = /^[A-Z][\w\s&,/'-]{0,60}$/;

// Strip styling tags, decode entities, collapse whitespace within a paragraph or list item.
const stripInline = (s: string): string =>
  s
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/?(?:span|strong|em|b|i|u)[^>]*>/gi, '')
    .replace(/<a[^>]*>([\s\S]*?)<\/a>/gi, '$1')
    .replace(/<[^>]+>/g, '')
    .replace(/[ \t]+/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .trim();

// Promote a heading-eligible single line ("**Section**", "Section:", "Section") into a heading
// block, otherwise emit a paragraph. Used for paragraphs that may carry markdown-in-text intent.
const lineToBlock = (line: string): DescriptionBlock => {
  const cleaned = line
    .replace(/^\*\*\s*/, '')
    .replace(/\s*\*\*\s*$/, '')
    .replace(/:\s*$/, '')
    .replace(/\*\*/g, '')
    .trim();
  if (cleaned.length > 0 && cleaned.length <= 60 && HEADING_RE.test(cleaned)) {
    return { kind: 'heading', text: cleaned };
  }
  return { kind: 'paragraph', text: line.replace(/\*\*/g, '').trim() };
};

const segmentToBlocks = (segment: string): DescriptionBlock[] => {
  const lines = segment
    .split(/\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0) return [];
  if (lines.length > 1 && lines.every((l) => /^[*•-]\s+/.test(l))) {
    return [{ kind: 'list', items: lines.map((l) => l.replace(/^[*•-]\s+/, '')) }];
  }
  if (lines.length === 1) return [lineToBlock(lines[0])];
  return [{ kind: 'paragraph', text: lines.join(' ').replace(/\*\*/g, '').trim() }];
};

// Rippling content varies wildly: some roles use real <h1-3>/<ul>/<li>, others store
// everything as a single styled <p> with markdown-in-text. Extract structural HTML at the
// tag level and fall back to text-block parsing only when a paragraph has no structure.
const parseRipplingDescription = (raw: string): DescriptionBlock[] => {
  const cleaned = raw
    .replace(/<\/?meta[^>]*>/gi, '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/&quot;/g, '"')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&#39;/g, "'");

  const blocks: DescriptionBlock[] = [];
  const tagRe = /<(h[1-6]|ul|ol|p)\b[^>]*>([\s\S]*?)<\/\1>/gi;

  for (const match of cleaned.matchAll(tagRe)) {
    const tag = match[1].toLowerCase();
    const inner = match[2];

    if (/^h[1-6]$/.test(tag)) {
      const text = stripInline(inner);
      if (text) blocks.push({ kind: 'heading', text });
      continue;
    }

    if (tag === 'ul' || tag === 'ol') {
      const items: string[] = [];
      for (const li of inner.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)) {
        const t = stripInline(li[1]);
        if (t) items.push(t);
      }
      if (items.length) blocks.push({ kind: 'list', items });
      continue;
    }

    // <p>: may be a single sentence, a heading-shaped phrase, or — for the Finance-style
    // recruiter-paste case — a whole multi-section blob with <br><br> dividers and
    // markdown bullets inside.
    const text = stripInline(inner);
    if (!text) continue;

    const segments = text
      .split(/\n{2,}/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (segments.length === 1) {
      blocks.push(...segmentToBlocks(segments[0]));
    } else {
      for (const seg of segments) blocks.push(...segmentToBlocks(seg));
    }
  }

  return blocks;
};

const DescriptionBlocks = ({ blocks }: { blocks: DescriptionBlock[] }) => (
  <Col gap="$20x">
    {blocks.map((block, idx) => {
      if (block.kind === 'heading') {
        return (
          <Text key={idx} tag="h3" typography="$subheaderXsmall" color="$contentPrimary">
            {block.text}
          </Text>
        );
      }
      if (block.kind === 'list') {
        return (
          <Col key={idx} gap="$10x" paddingLeft="$4x">
            {block.items.map((item, i) => (
              <Row key={i} gap="$8x" alignItems="flex-start">
                <Box paddingTop="$1x">
                  <Text tag="span" typography="$labelLarge" color="$contentSecondary">
                    •
                  </Text>
                </Box>
                <Box flexShrink={1}>
                  <Text tag="span" typography="$bodyLarge" color="$contentPrimary">
                    {item}
                  </Text>
                </Box>
              </Row>
            ))}
          </Col>
        );
      }
      return (
        <Text key={idx} tag="p" typography="$bodyLarge" color="$contentPrimary">
          {block.text}
        </Text>
      );
    })}
  </Col>
);

const formatPosted = (iso: string): string => {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (Number.isNaN(days)) return '';
  if (days < 1) return 'Posted today';
  if (days < 2) return 'Posted yesterday';
  if (days < 7) return `Posted ${days} days ago`;
  if (days < 30) return `Posted ${Math.floor(days / 7)} weeks ago`;
  return `Posted ${new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}`;
};

const dropUndefined = <T extends Record<string, unknown>>(obj: T): T =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;

const buildJobPostingSchema = (job: RipplingV2Job): object => {
  const rawLoc = job.workLocations?.[0];
  const loc = rawLoc ? locWorkplace(rawLoc) : undefined;
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org/',
    '@type': 'JobPosting',
    title: job.name,
    description: `${job.description.company}\n${job.description.role}`,
    datePosted: job.createdOn,
    url: jobAbsoluteUrl(job),
    directApply: false,
    hiringOrganization: {
      '@type': 'Organization',
      name: job.companyName,
      sameAs: 'https://www.opendoor.com',
      logo: 'https://www.opendoor.com/icons/opendoor-logo.svg',
    },
  };
  if (job.employmentType?.id) schema.employmentType = job.employmentType.id;
  if (loc) {
    const isRemote = loc.workplaceType === 'REMOTE';
    schema.jobLocationType = isRemote ? 'TELECOMMUTE' : undefined;
    schema.jobLocation = {
      '@type': 'Place',
      address: dropUndefined({
        '@type': 'PostalAddress',
        addressLocality: loc.city,
        addressRegion: loc.state,
        addressCountry: loc.country,
      }),
    };
  }
  return dropUndefined(schema);
};

const OpenPositionCard = ({ job }: { job: ListingJob }) => (
  <Box
    flexDirection="column"
    justifyContent="space-between"
    borderColor="$borderPrimary"
    borderStyle="solid"
    borderWidth="$default"
    borderRadius="$12x"
    paddingHorizontal="$20x"
    paddingVertical="$16x"
    gap="$8x"
    $largerThanSM={{ flexDirection: 'row', alignItems: 'center' }}
    hoverStyle={{ borderColor: '$borderStateHoverPrimary' }}
  >
    <Box width="100%" $largerThanSM={{ width: '50%' }}>
      <Link
        analyticsName="open-position"
        size="large"
        color="$contentAccentBrand"
        typography="$labelLarge"
        variant="primary"
        href={jobPath(job)}
        target="_blank"
      >
        {job.name}
      </Link>
    </Box>
    <Box width="100%" $largerThanSM={{ width: '50%' }}>
      <Text
        tag="p"
        typography="$bodyLarge"
        color="$contentSecondary"
        $largerThanSM={{ textAlign: 'right' }}
      >
        {job.locationLabels.length === 0 ? 'Multiple locations' : job.locationLabels.join(' · ')}
      </Text>
    </Box>
  </Box>
);

const ListingView = ({ resolved }: { resolved: ListingResolved }) => {
  const { departmentFilters, officeFilters, jobsByDepartment } = resolved;
  const [selectedDepartment, setSelectedDepartment] = useState<string>();
  const [selectedOffice, setSelectedOffice] = useState<string>();

  const filteredByDepartment =
    selectedDepartment && selectedDepartment !== 'all'
      ? jobsByDepartment.filter((d) => d.label === selectedDepartment)
      : jobsByDepartment;

  const filtered =
    selectedOffice && selectedOffice !== 'all'
      ? filteredByDepartment
          .map((d) => ({
            ...d,
            jobs: d.jobs.filter((j) => j.locationLabels.includes(selectedOffice)),
          }))
          .filter((d) => d.jobs.length > 0)
      : filteredByDepartment;

  return (
    <>
      <Box gap="$16x" marginBottom="$32x" $largerThanSM={{ flexDirection: 'row' }}>
        <Box
          width="100%"
          flexGrow={1}
          $largerThanSM={{ width: 285 }}
          $largerThanMD={{ flexGrow: 0 }}
        >
          <Dropdown
            title="Department"
            size="medium"
            aria-labelledby="Select Department"
            options={departmentFilters}
            placeholderOption={{ label: 'All Departments', value: 'all' }}
            onValueChange={(value) => setSelectedDepartment(value)}
          />
        </Box>
        <Box
          width="100%"
          flexGrow={1}
          $largerThanSM={{ width: 285 }}
          $largerThanMD={{ flexGrow: 0 }}
        >
          <Dropdown
            title="Office"
            size="medium"
            aria-labelledby="Select Office"
            options={officeFilters}
            placeholderOption={{ label: 'All Offices', value: 'all' }}
            onValueChange={(value) => setSelectedOffice(value)}
          />
        </Box>
      </Box>

      <Box marginVertical="$10x">
        {filtered.map((dept) => (
          <Box key={dept.label} marginBottom="$32x" gap="$20x">
            <Text tag="h2" typography="$headerSmall">
              {dept.label}
            </Text>
            <Col gap="$8x">
              {dept.jobs.map((job) => (
                <OpenPositionCard key={job.uuid} job={job} />
              ))}
            </Col>
          </Box>
        ))}
      </Box>
    </>
  );
};

const JobDetailView = ({ resolved }: { resolved: DetailResolved }) => {
  const { job } = resolved;

  if (!job) {
    return (
      <Col py="$50x" gap="$8x">
        <Text tag="h1" typography="$headerSmall">
          This role isn&rsquo;t available
        </Text>
        <Text tag="p" typography="$bodyLarge" color="$contentSecondary">
          The position may have been filled or removed.
        </Text>
        <Link
          analyticsName="back-to-listing"
          color="$contentAccentBrand"
          typography="$labelLarge"
          variant="primary"
          href="/careers/open-positions"
        >
          View all open positions →
        </Link>
      </Col>
    );
  }

  const allLocationNames = (job.workLocations ?? [])
    .map(locName)
    .filter((n): n is string => Boolean(n));
  let primaryLocation: string;
  if (allLocationNames.length === 0) {
    primaryLocation = 'Multiple locations';
  } else if (allLocationNames.length > 2) {
    primaryLocation = `${allLocationNames.length} locations`;
  } else {
    primaryLocation = allLocationNames.join(' · ');
  }
  const employment = job.employmentType?.id;
  const posted = formatPosted(job.createdOn);
  const applyHref = ripplingApplyUrl(job.uuid);
  const wordSet = (s: string): Set<string> =>
    new Set(
      s
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .split(/\s+/)
        .filter((w) => w.length > 2),
    );
  const titleWords = wordSet(job.name);
  const stripDuplicateTitle = (blocks: DescriptionBlock[]): DescriptionBlock[] =>
    blocks.filter((b) => {
      if (b.kind !== 'heading') return true;
      const headingWords = Array.from(wordSet(b.text));
      if (headingWords.length === 0) return true;
      const overlap = headingWords.filter((w) => titleWords.has(w)).length;
      const isDupe = overlap >= 2 && overlap >= headingWords.length * 0.7;
      return !isDupe;
    });
  const roleBlocks = stripDuplicateTitle(parseRipplingDescription(job.description.role));
  const companyBlocks = stripDuplicateTitle(parseRipplingDescription(job.description.company));

  const metaItems = [primaryLocation, employment, posted].filter(Boolean) as string[];

  return (
    <Col gap="$32x" $largerThanMD={{ width: 768 }}>
      <Col gap="$20x">
        <Text tag="p" typography="$labelSmall" color="$contentTertiary">
          {job.department.base_department.toUpperCase()}
        </Text>
        <Text tag="h1" typography="$displaySmall" color="$contentPrimary">
          {job.name}
        </Text>
        <Row gap="$8x" flexWrap="wrap" alignItems="center">
          {metaItems.map((item, i) => (
            <Row key={i} gap="$8x" alignItems="center">
              {i > 0 ? (
                <Text tag="span" typography="$bodyLarge" color="$contentTertiary">
                  ·
                </Text>
              ) : null}
              <Text tag="span" typography="$bodyLarge" color="$contentSecondary">
                {item}
              </Text>
            </Row>
          ))}
        </Row>
        <Button
          analyticsName="apply-now-hero"
          size="large"
          variant="primary"
          label="Apply now"
          href={applyHref}
          alignSelf="flex-start"
        />
      </Col>

      <Box height="$1x" backgroundColor="$borderPrimary" width="100%" />

      <DescriptionBlocks blocks={roleBlocks} />

      <Col paddingTop="$8x">
        <DescriptionBlocks blocks={companyBlocks} />
      </Col>

      <Col
        gap="$16x"
        padding="$32x"
        backgroundColor="$backgroundSecondary"
        borderRadius="$8x"
        alignItems="flex-start"
      >
        <Text tag="h3" typography="$subheaderMedium" color="$contentPrimary">
          Ready to apply?
        </Text>
        <Text tag="p" typography="$bodyLarge" color="$contentSecondary">
          You&rsquo;ll be redirected to our application portal to submit your resume and answer a
          few questions.
        </Text>
        <Button
          analyticsName="apply-now-footer"
          size="large"
          variant="primary"
          label="Apply for this role"
          href={applyHref}
          alignSelf="flex-start"
        />
      </Col>
    </Col>
  );
};

const OpenPositionsCTA = (entry: ILpComponentOpenPositions, resolvedData?: Resolved) => {
  const isDetail = entry.fields.pageType === 'Job Description';

  return (
    <LandingPageSectionContainer minHeight={400} paddingBottom="$36x">
      {isDetail ? (
        <JobDetailView
          resolved={{
            job: resolvedData?.job ?? null,
            jobPostingSchema: resolvedData?.jobPostingSchema ?? null,
            seoOverride: resolvedData?.seoOverride ?? null,
          }}
        />
      ) : (
        <ListingView
          resolved={{
            departmentFilters: resolvedData?.departmentFilters ?? [],
            officeFilters: resolvedData?.officeFilters ?? [],
            jobsByDepartment: resolvedData?.jobsByDepartment ?? [],
          }}
        />
      )}
    </LandingPageSectionContainer>
  );
};

const EMPTY_LISTING: ListingResolved = {
  departmentFilters: [],
  officeFilters: [],
  jobsByDepartment: [],
};

const fetchListing = async (): Promise<ListingResolved> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 5000);
  let jobs: RipplingV1Job[] = [];
  try {
    const res = await fetch(RIPPLING_BOARD_LIST_URL, { signal: controller.signal });
    if (res.status !== 200) return EMPTY_LISTING;
    jobs = (await res.json()) as RipplingV1Job[];
  } catch {
    return EMPTY_LISTING;
  } finally {
    clearTimeout(timer);
  }

  // Pass 1: collapse duplicate UUID rows (Rippling emits one row per (req, loc) for
  // multi-location reqs but reuses the UUID).
  const byUuid = new Map<string, ListingJob>();
  const offices = new Set<string>();
  for (const job of jobs) {
    const loc = job.workLocation?.label;
    if (loc) offices.add(loc);
    const existing = byUuid.get(job.uuid);
    if (existing) {
      if (loc && !existing.locationLabels.includes(loc)) existing.locationLabels.push(loc);
      continue;
    }
    byUuid.set(job.uuid, {
      uuid: job.uuid,
      name: job.name,
      department: job.department,
      locationLabels: loc ? [loc] : [],
    });
  }

  for (const job of byUuid.values()) {
    job.locationLabels.sort((a, b) => a.localeCompare(b));
  }

  const byDept = new Map<string, ListingJob[]>();
  for (const job of byUuid.values()) {
    const dept = job.department?.label ?? 'Other';
    if (!byDept.has(dept)) byDept.set(dept, []);
    byDept.get(dept)!.push(job);
  }

  const jobsByDepartment = Array.from(byDept.entries())
    .map(([label, list]) => ({
      label,
      jobs: list.sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const departmentFilters = jobsByDepartment.map((d) => ({ label: d.label, value: d.label }));
  const officeFilters = Array.from(offices)
    .sort((a, b) => a.localeCompare(b))
    .map((label) => ({ label, value: label }));

  return { departmentFilters, officeFilters, jobsByDepartment };
};

const fetchDetail = async (uuid: string): Promise<DetailResolved> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 5000);
  try {
    const res = await fetch(ripplingDetailUrl(uuid), { signal: controller.signal });
    if (res.status !== 200) return { job: null, jobPostingSchema: null, seoOverride: null };
    const job = (await res.json()) as RipplingV2Job;
    if (!job?.uuid || !job?.name || !job?.description) {
      return { job: null, jobPostingSchema: null, seoOverride: null };
    }
    const primaryLocation = job.workLocations?.[0]
      ? locName(job.workLocations[0]) || 'Multiple locations'
      : 'Multiple locations';
    const seoOverride: SeoOverride = {
      title: `${job.name} - Opendoor`,
      canonicalUrl: jobAbsoluteUrl(job),
      metaDescription: `${job.name} at Opendoor — ${primaryLocation}. Apply on opendoor.com.`,
      ogTitle: `${job.name} - Opendoor`,
      ogUrl: jobAbsoluteUrl(job),
      ogDescription: `${job.name} at Opendoor — ${primaryLocation}.`,
    };
    return { job, jobPostingSchema: buildJobPostingSchema(job), seoOverride };
  } catch (_err) {
    return { job: null, jobPostingSchema: null, seoOverride: null };
  } finally {
    clearTimeout(timer);
  }
};

const openPositionsLoader = async (
  entry: ILpComponentOpenPositions,
  _root?: unknown,
  pageProps?: { paginationIndex: number; url: string },
): Promise<Resolved> => {
  if (entry.fields.pageType === 'Job Description') {
    if (!pageProps?.url) return { job: null, jobPostingSchema: null, seoOverride: null };
    const uuid = extractJobUuid(pageProps.url);
    if (!uuid) return { job: null, jobPostingSchema: null, seoOverride: null };
    return fetchDetail(uuid);
  }
  return fetchListing();
};

const OpenPositions = {
  render: OpenPositionsCTA,
  loader: openPositionsLoader,
};

export default OpenPositions;
